/**
 * React hook for WebSocket management and real-time updates
 */

import { useEffect, useRef, useState, useCallback, DependencyList } from 'react';
import { WebSocketService, WebSocketMessage, initializeWebSocket, getWebSocketService } from '../services/websocket';
import { useAppDispatch } from './redux';
import { setConnectionStatus } from '../store/slices/realtimeSlice';

export interface UseWebSocketOptions {
  token?: string;
  autoConnect?: boolean;
  channels?: string[];
}

export interface UseWebSocketReturn {
  isConnected: boolean;
  service: WebSocketService | null;
  connect: () => void;
  disconnect: () => void;
  subscribe: (channel: string) => void;
  unsubscribe: (channel: string) => void;
  send: (message: WebSocketMessage) => void;
  onMessage: (type: string, handler: (message: WebSocketMessage) => void) => void;
  offMessage: (type: string, handler: (message: WebSocketMessage) => void) => void;
}

/**
 * Hook for managing WebSocket connections
 */
export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketReturn {
  const { token, autoConnect = true, channels = [] } = options;
  const [isConnected, setIsConnected] = useState(false);
  const [service, setService] = useState<WebSocketService | null>(null);
  const handlersRef = useRef<Map<string, (message: WebSocketMessage) => void>>(new Map());
  const dispatch = useAppDispatch();

  // Connection status handler
  const handleConnectionChange = useCallback((connected: boolean) => {
    setIsConnected(connected);
    // Update Redux store with connection status
    dispatch(setConnectionStatus(connected ? 'connected' : 'disconnected'));
  }, [dispatch]);

  // Initialize WebSocket service
  const connect = useCallback(() => {
    if (!token) {
      console.warn('WebSocket token not provided');
      return;
    }

    let wsService = getWebSocketService();
    if (!wsService) {
      wsService = initializeWebSocket(token);
    }
    setService(wsService);

    // Add connection listener
    wsService.onConnection(handleConnectionChange);

    // Connect only if not already connected or connecting
    if (!wsService.isConnected()) {
      wsService.connect();
    }

    // Auto-subscribe to channels
    if (channels.length > 0) {
      // Wait a bit for connection to establish
      setTimeout(() => {
        channels.forEach(channel => {
          wsService?.subscribe(channel);
        });
      }, 1000);
    }
  }, [token, channels, handleConnectionChange]);

  // Disconnect WebSocket
  const disconnect = useCallback(() => {
    const wsService = getWebSocketService();
    if (wsService) {
      wsService.offConnection(handleConnectionChange);
      wsService.disconnect();
    }
    setService(null);
    setIsConnected(false);
  }, [handleConnectionChange]);

  // Subscribe to channel
  const subscribe = useCallback((channel: string) => {
    const wsService = getWebSocketService();
    if (wsService) {
      wsService.subscribe(channel);
    }
  }, []);

  // Unsubscribe from channel
  const unsubscribe = useCallback((channel: string) => {
    const wsService = getWebSocketService();
    if (wsService) {
      wsService.unsubscribe(channel);
    }
  }, []);

  // Send message
  const send = useCallback((message: WebSocketMessage) => {
    const wsService = getWebSocketService();
    if (wsService) {
      wsService.send(message);
    }
  }, []);

  // Add message handler
  const onMessage = useCallback((type: string, handler: (message: WebSocketMessage) => void) => {
    const wsService = getWebSocketService();
    if (wsService) {
      wsService.onMessage(type, handler);
      handlersRef.current.set(`${type}_${handler.toString()}`, handler);
    }
  }, []);

  // Remove message handler
  const offMessage = useCallback((type: string, handler: (message: WebSocketMessage) => void) => {
    const wsService = getWebSocketService();
    if (wsService) {
      wsService.offMessage(type, handler);
      handlersRef.current.delete(`${type}_${handler.toString()}`);
    }
  }, []);

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect && token) {
      connect();
    }

    // Cleanup on unmount
    return () => {
      // Clean up message handlers
      const wsService = getWebSocketService();
      if (wsService) {
        handlersRef.current.forEach((handler: (message: WebSocketMessage) => void, key: string) => {
          const [type] = key.split('_');
          wsService.offMessage(type, handler);
        });
        wsService.offConnection(handleConnectionChange);
      }
    };
  }, [autoConnect, token, connect, handleConnectionChange]);

  return {
    isConnected,
    service,
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    send,
    onMessage,
    offMessage,
  };
}

/**
 * Hook for listening to specific message types
 */
export function useWebSocketMessage(
  messageType: string,
  handler: (message: WebSocketMessage) => void,
  deps: DependencyList = []
) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const wsService = getWebSocketService();
    if (!wsService) return undefined;

    const wrappedHandler = (message: WebSocketMessage) => {
      handlerRef.current(message);
    };

    wsService.onMessage(messageType, wrappedHandler);

    return () => {
      wsService.offMessage(messageType, wrappedHandler);
    };
  }, [messageType, ...deps]);
}

/**
 * Hook for real-time order updates
 */
export function useOrderUpdates() {
  const [orders, setOrders] = useState<any[]>([]);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  useWebSocketMessage('order_created', (message) => {
    console.log('Order created:', message.data);
    setOrders((prev: any[]) => [...prev, message.data]);
    setLastUpdate(new Date());
  });

  useWebSocketMessage('order_updated', (message) => {
    console.log('Order updated:', message.data);
    setOrders((prev: any[]) => prev.map((order: any) =>
      order.order_id === message.data.order_id
        ? { ...order, ...message.data.changes }
        : order
    ));
    setLastUpdate(new Date());
  });

  useWebSocketMessage('order_status_changed', (message) => {
    console.log('Order status changed:', message.data);
    setOrders((prev: any[]) => prev.map((order: any) =>
      order.order_id === message.data.order_id
        ? { ...order, status: message.data.new_status }
        : order
    ));
    setLastUpdate(new Date());
  });

  return { orders, lastUpdate };
}

/**
 * Hook for real-time employee updates
 */
export function useEmployeeUpdates() {
  const [employees, setEmployees] = useState<any[]>([]);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  useWebSocketMessage('employee_created', (message) => {
    console.log('Employee created:', message.data);
    setEmployees((prev: any[]) => [...prev, message.data]);
    setLastUpdate(new Date());
  });

  useWebSocketMessage('employee_updated', (message) => {
    console.log('Employee updated:', message.data);
    setEmployees((prev: any[]) => prev.map((employee: any) =>
      employee.employee_id === message.data.employee_id
        ? { ...employee, ...message.data.changes }
        : employee
    ));
    setLastUpdate(new Date());
  });

  useWebSocketMessage('assignment_created', (message) => {
    console.log('Assignment created:', message.data);
    setLastUpdate(new Date());
  });

  return { employees, lastUpdate };
}

/**
 * Hook for real-time dashboard updates
 */
export function useDashboardUpdates() {
  const [metrics, setMetrics] = useState<any>({});
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  useWebSocketMessage('dashboard_update', (message) => {
    console.log('Dashboard update:', message.data);
    setMetrics((prev: any) => ({ ...prev, ...message.data }));
    setLastUpdate(new Date());
  });

  useWebSocketMessage('dashboard_metrics_update', (message) => {
    console.log('Dashboard metrics update:', message.data);
    setMetrics(message.data);
    setLastUpdate(new Date());
  });

  return { metrics, lastUpdate };
}

/**
 * Hook for real-time notifications
 */
export function useNotifications() {
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useWebSocketMessage('user_notification', (message) => {
    console.log('User notification:', message.data);
    const notification = {
      id: Date.now(),
      ...message.data,
      timestamp: new Date(),
      read: false,
    };
    setNotifications((prev: any[]) => [notification, ...prev]);
    setUnreadCount((prev: number) => prev + 1);
  });

  useWebSocketMessage('system_notification', (message) => {
    console.log('System notification:', message.data);
    const notification = {
      id: Date.now(),
      ...message.data,
      timestamp: new Date(),
      read: false,
      type: 'system',
    };
    setNotifications((prev: any[]) => [notification, ...prev]);
    setUnreadCount((prev: number) => prev + 1);
  });

  useWebSocketMessage('assignment_notification', (message) => {
    console.log('Assignment notification:', message.data);
    const notification = {
      id: Date.now(),
      ...message.data,
      timestamp: new Date(),
      read: false,
      type: 'assignment',
    };
    setNotifications((prev: any[]) => [notification, ...prev]);
    setUnreadCount((prev: number) => prev + 1);
  });

  const markAsRead = useCallback((notificationId: number) => {
    setNotifications((prev: any[]) => prev.map((notification: any) =>
      notification.id === notificationId
        ? { ...notification, read: true }
        : notification
    ));
    setUnreadCount((prev: number) => Math.max(0, prev - 1));
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev: any[]) => prev.map((notification: any) => ({ ...notification, read: true })));
    setUnreadCount(0);
  }, []);

  return { 
    notifications, 
    unreadCount, 
    markAsRead, 
    markAllAsRead 
  };
}
